import React, { useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, ArrowRightIcon, StarIcon, MessageCircleIcon, LinkIcon, RefreshCwIcon, ShieldCheckIcon, ThumbsUpIcon, ChevronDownIcon, BuildingIcon, ZapIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function ReputationManagementPage() {
  const faqs = [{
    question: 'How does automated review requesting work?',
    answer: 'When a job is marked as complete in your CRM or invoicing software, our system automatically sends a polite, customized text message and email to the customer thanking them for their business and asking for a review with a direct link.'
  }, {
    question: 'Can I respond to reviews from one dashboard?',
    answer: 'Yes. You can view and reply to reviews from Google, Facebook, and major review platforms all from a single unified dashboard or directly from our mobile app. No more logging into multiple accounts.'
  }, {
    question: 'What platforms do you manage reviews on?',
    answer: 'We focus on the platforms that matter most for local service businesses: Google Business Profile, Facebook, and the major review sites for your industry. We can also integrate with industry-specific platforms depending on your needs.'
  }, {
    question: 'Do I need to be on every review platform?',
    answer: 'No. We analyze your specific market and industry to determine where your customers are actually looking. Usually, dominating Google plus one or two key review platforms is all a local service business needs to win their market.'
  }];
  const FAQItem = ({
    question,
    answer
  }: {
    question: string;
    answer: string;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    return <div className="border-b border-gray-800 last:border-0" data-id="element-2571">
        <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-2572">
          <span className="text-lg font-bold text-white group-hover:text-yellow-500 transition-colors" data-id="element-2573">
            {question}
          </span>
          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-yellow-500' : ''}`} data-id="element-2574" />
        </button>
        <AnimatePresence data-id="element-2575">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-2576">
              <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-2577">{answer}</p>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  return <div className="bg-viridia-black min-h-screen" data-id="element-2578">
      <SEOHead title="Reputation Management | Automate Reviews | Viridia Analytics" description="Turn happy customers into public proof. Manage reviews across platforms, automate review requests, and build trust for your local service business." canonical="/reputation-management" keywords="reputation management Phoenix, marketing agency phoenix, HVAC, painters, landscapers, contractors, window installers, automated review requests, get more Google reviews, local business reputation, review management software" faq={faqs} data-id="element-2579" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden lg:min-h-[90vh] flex items-center" data-id="element-2580">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-2581">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-yellow-500/10 blur-[120px] animate-pulse-slow" data-id="element-2582"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-2583">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-2584">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-2585">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} data-id="element-2586">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 text-sm font-semibold tracking-wide mb-6" data-id="element-2587">
                  REPUTATION MANAGEMENT
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-2588">
                  Turn Happy Customers Into{' '}
                  <span className="text-yellow-500" data-id="element-2589">Public Proof</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-2590">
                  Reviews are not just vanity metrics; they are conversion
                  leverage. We automate review requests after completed jobs to
                  build trust and help you win more local searches.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-2591">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300 shadow-[0_0_20px_rgba(16,185,129,0.2)]" data-id="element-2592">
                    Book a Strategy Call
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-2593">
                    View Packages
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Visual - Reputation Control Center */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="relative hidden lg:block h-[500px]" data-id="element-2594">
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-viridia-surface rounded-2xl border border-gray-800 shadow-2xl overflow-hidden p-6 flex flex-col" data-id="element-2595">
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-800" data-id="element-2596">
                  <div className="text-sm font-bold text-white flex items-center" data-id="element-2597">
                    <StarIcon className="w-4 h-4 text-yellow-500 mr-2 fill-current" data-id="element-2598" />
                    Reputation Control Center
                  </div>
                  <div className="bg-green-500/10 text-green-400 text-xs px-2 py-1 rounded font-medium" data-id="element-2599">
                    System Active
                  </div>
                </div>

                <div className="flex-grow relative" data-id="element-2600">
                  {/* Floating Review Cards */}
                  <motion.div initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.5
                }} className="absolute top-4 left-4 right-12 bg-white rounded-lg p-4 shadow-lg z-20" data-id="element-2601">
                    <div className="flex justify-between items-start mb-2" data-id="element-2602">
                      <div className="flex text-yellow-400" data-id="element-2603">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-current" data-id="element-2604" />)}
                      </div>
                      <span className="text-xs text-gray-400 font-medium" data-id="element-2605">
                        Google • 2h ago
                      </span>
                    </div>
                    <p className="text-sm text-gray-800 font-medium mb-1" data-id="element-2606">
                      "They responded instantly and fixed the issue the same
                      day. Highly recommend!"
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2607">- Sarah Jenkins</p>
                  </motion.div>

                  <motion.div initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.7
                }} className="absolute top-32 left-12 right-4 bg-viridia-black border border-gray-700 rounded-lg p-4 shadow-xl z-10" data-id="element-2608">
                    <div className="flex justify-between items-start mb-2" data-id="element-2609">
                      <div className="flex text-yellow-500" data-id="element-2610">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-current" data-id="element-2611" />)}
                      </div>
                      <span className="text-xs text-gray-500 font-medium" data-id="element-2612">
                        Review Platform • 1h ago
                      </span>
                    </div>
                    <p className="text-sm text-gray-300 font-medium mb-1" data-id="element-2613">
                      "Very professional crew. The estimate was accurate and the
                      work was top notch."
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2614">- Michael T.</p>
                  </motion.div>

                  {/* Automation Flow Indicator */}
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center" data-id="element-2615">
                    <div className="bg-viridia-surface border border-gray-800 rounded-full px-4 py-2 flex items-center gap-3 shadow-lg" data-id="element-2616">
                      <div className="flex items-center gap-1 text-xs text-gray-400" data-id="element-2617">
                        <CheckCircleIcon className="w-3 h-3 text-viridia" data-id="element-2618" /> Job
                        Complete
                      </div>
                      <ArrowRightIcon className="w-3 h-3 text-gray-600" data-id="element-2619" />
                      <div className="flex items-center gap-1 text-xs text-gray-400" data-id="element-2620">
                        <MessageCircleIcon className="w-3 h-3 text-blue-400" data-id="element-2621" />{' '}
                        SMS Sent
                      </div>
                      <ArrowRightIcon className="w-3 h-3 text-gray-600" data-id="element-2622" />
                      <div className="flex items-center gap-1 text-xs text-yellow-500 font-bold" data-id="element-2623">
                        <StarIcon className="w-3 h-3 fill-current" data-id="element-2624" /> 5-Star
                        Review
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-2625">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2626">
          <ScrollReveal data-id="element-2627">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2628">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2629">
                Why Reviews Matter Financially
              </h2>
              <p className="text-xl text-gray-400" data-id="element-2630">
                In local services, trust is everything. If your competitor has
                100 more reviews than you, they are getting the call.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2631">
            {[{
            icon: <StarIcon className="w-6 h-6 text-white" data-id="element-2632" />,
            title: 'Conversion Leverage',
            desc: 'A strong review profile increases the conversion rate of every ad dollar you spend.',
            color: 'bg-yellow-500'
          }, {
            icon: <ShieldCheckIcon className="w-6 h-6 text-white" data-id="element-2633" />,
            title: 'Local Trust',
            desc: 'Customers hire the business that looks the most reliable. Reviews are public proof of your reliability.',
            color: 'bg-blue-500'
          }, {
            icon: <RefreshCwIcon className="w-6 h-6 text-white" data-id="element-2634" />,
            title: 'Automated Workflows',
            desc: 'We tie review requests directly to your CRM. When a job is done, the request goes out automatically.',
            color: 'bg-viridia'
          }].map((feature, i) => <ScrollReveal key={i} delay={i * 0.1} data-id="element-2635">
                <div className="bg-viridia-black rounded-2xl p-8 border border-gray-800 h-full hover:border-gray-700 transition-colors" data-id="element-2636">
                  <div className={`w-12 h-12 rounded-xl ${feature.color}/20 flex items-center justify-center mb-6`} data-id="element-2637">
                    <div className={`w-8 h-8 rounded-full ${feature.color} flex items-center justify-center`} data-id="element-2638">
                      {feature.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-2639">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-2640">
                    {feature.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section className="py-24 bg-viridia-black" data-id="element-2641">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2642">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2643">
            <ScrollReveal data-id="element-2644">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-2645">
                The Automated Review Loop
              </h2>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-2646">
                Asking for reviews manually is awkward and inconsistent. We
                build a system that asks for you, exactly when the customer is
                happiest.
              </p>

              <div className="space-y-8" data-id="element-2647">
                <div className="flex items-start" data-id="element-2648">
                  <div className="flex-shrink-0 mr-4" data-id="element-2649">
                    <div className="w-10 h-10 rounded-full bg-viridia/10 border border-viridia/30 flex items-center justify-center text-viridia font-bold" data-id="element-2650">
                      1
                    </div>
                  </div>
                  <div data-id="element-2651">
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-2652">
                      Trigger on Completion
                    </h3>
                    <p className="text-gray-400" data-id="element-2653">
                      When your team marks a job as complete or paid in your
                      CRM, the automation begins.
                    </p>
                  </div>
                </div>

                <div className="flex items-start" data-id="element-2654">
                  <div className="flex-shrink-0 mr-4" data-id="element-2655">
                    <div className="w-10 h-10 rounded-full bg-viridia/10 border border-viridia/30 flex items-center justify-center text-viridia font-bold" data-id="element-2656">
                      2
                    </div>
                  </div>
                  <div data-id="element-2657">
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-2658">
                      Smart Request Sent
                    </h3>
                    <p className="text-gray-400" data-id="element-2659">
                      A personalized text and email are sent thanking them for
                      their business and providing a direct link to review.
                    </p>
                  </div>
                </div>

                <div className="flex items-start" data-id="element-2660">
                  <div className="flex-shrink-0 mr-4" data-id="element-2661">
                    <div className="w-10 h-10 rounded-full bg-viridia/10 border border-viridia/30 flex items-center justify-center text-viridia font-bold" data-id="element-2662">
                      3
                    </div>
                  </div>
                  <div data-id="element-2663">
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-2664">
                      Monitor & Respond
                    </h3>
                    <p className="text-gray-400" data-id="element-2665">
                      New reviews appear in your unified dashboard, allowing you
                      to respond quickly and professionally.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="left" data-id="element-2666">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 shadow-2xl" data-id="element-2667">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-800" data-id="element-2668">
                  <h3 className="text-white font-bold" data-id="element-2669">Automated SMS</h3>
                  <span className="text-xs text-gray-500" data-id="element-2670">Sent at 4:30 PM</span>
                </div>
                <div className="space-y-4" data-id="element-2671">
                  <div className="flex justify-end" data-id="element-2672">
                    <div className="bg-blue-600 text-white p-3 rounded-2xl rounded-tr-sm max-w-[80%] text-sm" data-id="element-2673">
                      Hi John, thanks for choosing Viridia HVAC! We hope you're
                      happy with your new AC unit. If you have a minute, we'd
                      love your feedback: https://g.page/r/review
                    </div>
                  </div>
                  <div className="flex justify-start" data-id="element-2674">
                    <div className="bg-gray-800 text-white p-3 rounded-2xl rounded-tl-sm max-w-[80%] text-sm" data-id="element-2675">
                      You guys were great. Just left a 5-star review!
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Best Fit Section */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-2676">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2677">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-2678">
            <ScrollReveal data-id="element-2679">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-2680">
                Who is this for?
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-2681">
                Our reputation management system is designed for local service
                businesses that want to turn their happy customers into a
                marketing asset.
              </p>
              <ul className="space-y-4" data-id="element-2682">
                <li className="flex items-start gap-3" data-id="element-2683">
                  <BuildingIcon className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" data-id="element-2684" />
                  <div data-id="element-2685">
                    <h4 className="font-bold text-white" data-id="element-2686">Local Contractors</h4>
                    <p className="text-sm text-gray-400" data-id="element-2687">
                      Businesses that rely on local trust to win jobs.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-2688">
                  <ZapIcon className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" data-id="element-2689" />
                  <div data-id="element-2690">
                    <h4 className="font-bold text-white" data-id="element-2691">
                      Businesses Using Our CRM
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-2692">
                      The review automation ties perfectly into our CRM,
                      triggering requests automatically when jobs are completed.
                    </p>
                  </div>
                </li>
              </ul>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-2693">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8" data-id="element-2694">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-2695">
                  Related Services
                </h3>
                <div className="space-y-4" data-id="element-2696">
                  <Link to="/crm-business-tools" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-2697">
                    <div className="font-bold text-white mb-1" data-id="element-2698">
                      CRM & Business Tools
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-2699">
                      Trigger review requests automatically from your pipeline.
                    </div>
                  </Link>
                  <Link to="/web-design-seo" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-2700">
                    <div className="font-bold text-white mb-1" data-id="element-2701">
                      Conversion Websites
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-2702">
                      Showcase your 5-star reviews on a site built to convert.
                    </div>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-2703">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-2704">
          <ScrollReveal data-id="element-2705">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-2706">
              Frequently Asked Questions
            </h2>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2707">
          <ScrollReveal delay={0.2} data-id="element-2708">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-2709">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-2710" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-2711">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2712">
          <ScrollReveal data-id="element-2713">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-2714">
              Start building local trust
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-2715">
              Book a strategy call to see how we can automate your review
              requests and help you win more local searches.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-viridia text-white hover:bg-viridia-dark transition-all duration-300 shadow-[0_0_20px_rgba(16,185,129,0.2)]" data-id="element-2716">
              Book a Strategy Call <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2717" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}